/*
 * operations Sagas
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 02-Jul-2024
 */
import { all, call, put, select, take } from 'redux-saga/effects'
import moment from 'moment'
import * as api from 'Api/operations/operations'
import * as actions from '../actions/operations'
import { doPopup } from 'Store/actions/popup'
import {
  getFormikForm,
  getUserMerchantId,
  getUserRoles,
  getUserTenantId,
  getOperationTerminalId,
  getOrderId
} from 'Store/selectors'
import {
  deleteEmptyValues,
  userRolesString,
  exportDownload,
  errorMessageManager,
  isJsonResponse,
  // buildRSQLQuery
} from 'Utility/globalMethods'
import { momentLongDateFormat } from 'Utility/dateMethods'
import { doLocalLoading } from 'Store/actions/loading'
import formattedMessage from 'Route/OrderManagement/modules/languageModule'
import { formatNumberString, parseStringNumber } from 'Utility/numberMethods'
import { getOrderDetailsSaga, orderSearchSaga } from 'Route/OrderManagement/modules/OrderManagementSagas'

const popupSuccessResult = {
  title: formattedMessage.exportResultTitle,
  isSuccess: true
}

const popupErrorResult = {
  title: formattedMessage.exportResultFailedTitle,
  isError: true
}

export function* getOperationDetailsSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  yield put(doLocalLoading({ id: 'OperationDetails', value: true }))
  const terminalData = yield call(getOperationTerminalSaga, payload)

  const params = deleteEmptyValues({
    operationId: payload,
    terminalId: terminalData?.id,
    merchantId,
    tenantId,
    roles: userRolesString(roleList)
  })

  yield put(actions.doGetOperationDetails.request({ ...params }))
  const { error, response } = yield call(api.callGetOperationDetail, { ...params })

  if (!error) {
    yield put(actions.doGetOperationDetails.success(response, response.data))
  } else {
    yield put(actions.doGetOperationDetails.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'OperationDetails', value: false }))
}

export function* getOperationsSaga(payload) {
  const { page = 1, timeStamp, sorting } = payload || {}
  const form = yield select((state) => getFormikForm(state, 'OperationFilterForm'))

  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roles = yield select(getUserRoles)
  const { advancedFilters, ...baseForm } = form || {}
  const { terminalId, paymentMethod, ...otherFilters } = advancedFilters || {}

  const orderByMap = {
    date: 'OPERATION_TIME',
    amount: 'OPERATION_AMOUNT'
  }
  const sort={orderBy: orderByMap[sorting?.orderBy], sortIn: sorting?.sortIn}

  let paymentMethodValue

  switch (paymentMethod) {
  case 'GOOGLEPAY':
    paymentMethodValue = 'GOOGLE_PAY'
    break
  case 'APPLEPAY':
    paymentMethodValue = 'APPLE_PAY'
    break
  case 'MASTERCARD':
    paymentMethodValue = 'MC'
    break
  default:
    paymentMethodValue = paymentMethod
  }

  // const baseFormExcludedKeys = ['fromTime', 'toTime', 'minAmount', 'maxAmount', 'advancedFilters']
  // const advancedFormExcludedKeys = ['terminalId', 'bonusMinAmount', 'bonusMaxAmount'] //ATTENZIONE: rinominare correttamente 'bonusMinAmount', 'bonusMaxAmount' e
  // posizionarli all'interno dell'oggetto 'advancedFilters' per far sì che la validazione di questi campi funzioni
  // const pushBaseRange = (min, max, field, array) => {
  //   if (!field || !array) return
  //   if (min && max) {
  //     array.push({
  //       field,
  //       operator: 'bt',
  //       value: [min, max]
  //     })
  //   } else {
  //     if (min) {
  //       array.push({
  //         field,
  //         operator: 'ge',
  //         value: min
  //       })
  //     }
  //     if (max) {
  //       array.push({
  //         field,
  //         operator: 'le',
  //         value: max
  //       })
  //     }
  //   }
  // }
  // const baseRsqlArray = Object.entries(baseForm || {})
  //   .filter(
  //     ([key, value]) => value !== null && value !== undefined && value !== '' && !baseFormExcludedKeys.includes(key)
  //   )
  //   .map(([key, value]) => {
  //     if (value === null || value === undefined || value === '') return null
  //     return {
  //       field: key,
  //       operator: '==',
  //       value: value
  //     }
  //   })
  //   .filter(Boolean)

  // pushBaseRange(
  //   baseForm?.fromTime ? italianDateConverter(form.fromTime, 'start') : null,
  //   baseForm?.toTime ? italianDateConverter(form.toTime, 'end') : null,
  //   'periodOfTime', // TODO: define a name with BE
  //   baseRsqlArray
  // )
  // pushBaseRange(
  //   baseForm?.minAmount ? parseStringNumber(baseForm.minAmount) : null,
  //   baseForm?.maxAmount ? parseStringNumber(baseForm.maxAmount) : null,
  //   'amount', // TODO: define a name with BE
  //   baseRsqlArray
  // )

  // const advancedRsqlArray = Object.entries(advancedFilters || {})
  //   .filter(
  //     ([key, value]) => value !== null && value !== undefined && value !== '' && !advancedFormExcludedKeys.includes(key)
  //   )
  //   .flatMap(([key, value]) => {
  //     if (value === null || value === undefined || value === '') return []
  //     switch (key) {
  //       case 'paymentMethod':
  //         return [
  //           {
  //             field: 'paymentCircuit',
  //             operator: '==',
  //             value: !['ALL', 'CARD', 'APM'].includes(paymentMethodValue) ? paymentMethodValue : undefined
  //           },
  //           {
  //             field: 'paymentMethod',
  //             operator: '==',
  //             value: ['CARD', 'APM'].includes(paymentMethodValue) ? paymentMethodValue : undefined
  //           }
  //         ].filter((item) => item.value !== undefined)
  //       default:
  //         return [
  //           {
  //             field: key,
  //             operator: '==',
  //             value: value
  //           }
  //         ]
  //     }
  //   })
  //   .filter(Boolean)

  // pushBaseRange(
  //   advancedFilters?.bonusMinAmount ? parseStringNumber(advancedFilters.bonusMinAmount) : null,
  //   advancedFilters?.bonusMaxAmount ? parseStringNumber(advancedFilters.bonusMaxAmount) : null,
  //ATTENZIONE: rinominare correttamente 'bonusMinAmount', 'bonusMaxAmount' per come sono dichiarati all'interno del file yup
  //   'bonusAmount', // TODO: define a name with BE
  //   advancedRsqlArray
  // )

  // const rsqlArray = [...baseRsqlArray, ...advancedRsqlArray]

  // const options = deleteEmptyValues({
  //   sort: sorting?.orderBy && sorting?.sortIn ? `${sorting?.orderBy},${sorting?.sortIn.toLowerCase()}` : '',
  //   pageSize: 10,
  //   page: page > 0 ? page : null,
  //   timeStamp: timeStamp, // TODO: define with BE!
  //   maxRecords: 10 //??????????????
  // })

  // const params = deleteEmptyValues({
  //   rsqlString: buildRSQLQuery(rsqlArray, options),
  //   headers: {
  //     tenantId,
  //     merchantId,
  //     roles: userRolesString(roles),
  //     terminalId: terminalId && terminalId !== 'ALL' ? terminalId : ''
  //   }
  // })
  const params = {
    headers: deleteEmptyValues({
      tenantId,
      merchantId,
      roles: userRolesString(roles),
      terminalId: terminalId && terminalId !== 'ALL' ? terminalId : ''
    }),
    queryParams: deleteEmptyValues({
      ...baseForm,
      ...otherFilters,
      fromTime: form.fromTime ? moment(form.fromTime, momentLongDateFormat()).startOf('day').toISOString() : null, // necessary to convert to ISO string because the BE expects this format
      toTime: form.toTime ? moment(form.toTime, momentLongDateFormat()).endOf('day').toISOString() : null, // necessary to convert to ISO string because the BE expects this format
      minAmount: form.minAmount ? parseStringNumber(form.minAmount) : null,
      maxAmount: form.maxAmount ? parseStringNumber(form.maxAmount) : null,
      paymentCircuit: !['ALL', 'CARD', 'APM'].includes(paymentMethodValue) ? paymentMethodValue : undefined,
      paymentMethod: ['CARD', 'APM'].includes(paymentMethodValue) ? paymentMethodValue : undefined,
      bonusAmountFrom: advancedFilters?.bonusAmountFrom ? parseStringNumber(advancedFilters.bonusAmountFrom) : null,
      bonusAmountTo: advancedFilters?.bonusAmountTo ? parseStringNumber(advancedFilters.bonusAmountTo) : null,
      bonusParticipation: advancedFilters?.bonusParticipation !== 'ALL' ? advancedFilters?.bonusParticipation : '',
      // pagination
      pageNumber: page > 0 ? page : null,
      pageSize: 10,
      maxRecords: 10,
      ...sort,
      timeStamp: timeStamp
    })
  }

  yield put(doLocalLoading({ id: 'OperationsGetOperations', value: true }))
  yield put(actions.doGetOperations.request({ ...params }))
  const { error, response } = yield call(api.callGetOperations, params)

  if (!error) {
    yield put(actions.doGetOperations.success(response, response.data))
  } else {
    yield put(actions.doGetOperations.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'OperationsGetOperations', value: false }))
}

export function* getParametersSaga() {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  const params = {
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(roleList)
    }
  }

  yield put(actions.doGetParameters.request({ ...params }))
  const { error, response } = yield call(api.callGetParameters, params)

  if (!error) {
    yield put(actions.doGetParameters.success(response, response.data))
  } else {
    yield put(actions.doGetParameters.failure(error, response))
  }
}

export function* getOperationActionsSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)
  const operationId = payload

  yield put(doLocalLoading({ id: 'OperationGetActions', value: true }))
  const terminalData = yield call(getOperationTerminalSaga, operationId)

  const params = deleteEmptyValues({
    id: operationId,
    terminalId: terminalData?.id,
    tenantId,
    merchantId,
    roles: userRolesString(roleList)
  })

  yield put(actions.getOperationActions.request({ ...params }))
  const { error, response } = yield call(api.callGetOperationActions, params)

  if (!error) {
    yield put(actions.getOperationActions.success(response.data))
  } else {
    yield put(actions.getOperationActions.failure(error, response))
  }

  yield put(doLocalLoading({ id: 'OperationGetActions', value: false }))
}

export function* getPaymentMethodsListSaga(payload) {
  const terminalId = payload // TODO: Check if terminalId is required
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    tenantId,
    merchantId,
    roles: userRolesString(roleList),
    terminalId
  })

  yield put(actions.getPaymentMethodsList.request(params))
  yield put(doLocalLoading({ id: 'OperationPaymentMethodsListLoader', value: true }))

  const { error, response } = yield call(api.getPaymentMethodsList, params)

  if (!error) {
    yield put(actions.getPaymentMethodsList.success(response.data))
  } else {
    yield put(actions.getPaymentMethodsList.failure(response, error))
  }
  yield put(doLocalLoading({ id: 'OperationPaymentMethodsListLoader', value: false }))
}

export function* getOperationTerminalSaga(operationId) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    operationId,
    tenantId,
    merchantId,
    roles: userRolesString(roleList)
  })

  yield put(actions.retriveOperationTerminal.request({ ...params }))

  const { error, response } = yield call(api.callGetOperationTerminalId, { ...params })

  if (!error) {
    yield put(actions.retriveOperationTerminal.success(response.data))
    return response.data
  } else {
    yield put(actions.retriveOperationTerminal.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'OperationGetActions', value: false }))
  yield put(doLocalLoading({ id: 'OperationDetails', value: false }))
}

export function* operationsExportSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    queryParams: {
      fromDate: payload?.date?.from,
      toDate: moment(payload?.date.to).set('second', 59).toISOString()
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(rolesList)
    },
    format: payload.format
  })
  yield put(doLocalLoading({ id: 'exportOperations', value: true }))
  yield put(actions.retriveOperationsExportFile.request(params))
  const { error, response } = yield call(api.callRetrieveOperationsExportFile, params)
  if (!error) {
    yield put(actions.retriveOperationsExportFile.success(response.data))
    if (response?.data?.size === 0) {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionEmpty
        })
      ) // TODO: Add , outcome: 'failure' to the payload
    } else {
      const fileName = response?.headers?.['content-disposition']
        ? response.headers['content-disposition'].match(/filename="(.+)"/)[1]
        : `XPay_data.${
          payload?.format === 'csv' ? 'zip' : 'xlsx'
        }`
      const fileType = response?.headers?.['content-type']
        ? response.headers['content-type']
        : payload?.format === 'csv'
          ? 'application/zip'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      exportDownload(response.data, fileName, fileType)
      yield put(doPopup(''))
    }
  } else {
    if (error.status === 400 && error?.data?.errors[0]?.code === 'PS0135') {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionExcede
        })
      ) // TODO: Add , outcome: 'failure' to the payload
      yield put(actions.retriveOperationsExportFile.failure(response, { ...error, ignoreSystemError: true }))
    } else {
      yield put(doPopup(''))
      yield put(actions.retriveOperationsExportFile.failure(response, error))
    }
  }
  yield put(doLocalLoading({ id: 'exportOperations', value: false }))
}
export function* captureRefundOperationSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)
  const terminalId = yield select(getOperationTerminalId)
  const form = yield select((state) => getFormikForm(state, 'OperationActionsForm'))

  const { operationId, amount, action } = form

  const parsedAmount = parseStringNumber(formatNumberString(amount))

  const params = deleteEmptyValues({
    terminalId,
    merchantId,
    tenantId,
    roles: userRolesString(roleList),
    operationId,
    payload:
      action === 'INCREMENTAL'
        ? {
          originalOperationId: operationId,
          amount: String(parsedAmount),
          currency: payload.currency
        }
        : { 
          amount: parsedAmount, 
          currency: payload.currency 
        }
  })

  if (action === 'INCREMENTAL') {
    params.payload.description = ''
  }

  let apiCall = null
  let reduxAction = null

  if (action === 'CAPTURE') {
    apiCall = api.callCaptureOperation
    reduxAction = actions.operationCapture
  }
  if (action === 'REFUND' || action === 'VOID') {
    apiCall = api.callRefundOperation
    reduxAction = actions.operationRefund
  }

  if (action === 'CANCEL' || action === 'CANCEL_REFUND') {
    apiCall = api.callCancelOperation
    reduxAction = actions.operationCancel
  }

  if (action === 'INCREMENTAL') {
    apiCall = api.callIncrementalOperation
    reduxAction = actions.operationIncremental
  }

  if (apiCall) {
    yield put(doLocalLoading({ id: 'operationAction', value: true }))
    yield put(reduxAction.request(params))
    const { error, response } = yield call(apiCall, params)
    if (!error) {
      if (isJsonResponse(response)) {
        yield put(reduxAction.success(response.data))
        if (payload?.isOperation) {
          yield call(getOperationsSaga)
        } else {
          const orderId = yield select(getOrderId)
          yield call(getOrderDetailsSaga, { orderId })
          yield call(orderSearchSaga)
        }
        yield put(doPopup(''))
        yield put(doPopup('RESULT_POPUP', popupSuccessResult))
        yield put(actions.doResetActionsMenu())
      } else {
        yield put(doPopup(''))
        yield put(
          doPopup('RESULT_POPUP', {
            ...popupErrorResult,
            description: formattedMessage.errorManagement.default
          })
        )
        yield put(reduxAction.failure(response, { ...error, ignoreSystemError: true }))
      }
    } else {
      const description = error?.data?.errors?.[0]?.code
        ? formattedMessage.errorManagement[errorMessageManager(error.data.errors[0].code)]
        : formattedMessage.errorManagement.default
      yield put(doPopup(''))
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: description
        })
      )
      yield put(reduxAction.failure(response, { ...error, ignoreSystemError: true }))
    }
    yield put(doLocalLoading({ id: 'operationAction', value: false }))
  }
}
// @generator saga

// @generator saga:watch

export function* watchCaptureRefundOperation() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_OPERATION_CAPTURE_REFUND)
    yield call(captureRefundOperationSaga, payload)
  }
}

export function* watchGetOperationTerminal() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_GET_OPERATION_TERMINAL)
    yield call(getOperationTerminalSaga, payload)
  }
}

export function* watchGetOperationActions() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_OPERATION_ACTIONS)
    yield call(getOperationActionsSaga, payload)
  }
}

export function* watchGetOperationDetails() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_OPERATION_DETAILS)
    yield call(getOperationDetailsSaga, payload)
  }
}

export function* watchGetOperations() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_OPERATIONS)
    yield call(getOperationsSaga, payload)
  }
}

export function* watchGetParameters() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_PARAMETERS)
    yield call(getParametersSaga, payload)
  }
}

export function* watchGetPaymentMethodsList() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_PAYMENT_METHODS_LIST)
    yield call(getPaymentMethodsListSaga, payload)
  }
}

export function* watchOperationsExport() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_EXPORT_OPERATIONS)
    yield call(operationsExportSaga, { ...payload })
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* rootSaga() {
  yield all([
    watchGetOperationTerminal(),
    watchGetOperationDetails(),
    watchGetOperations(),
    watchGetParameters(),
    watchGetOperationActions(),
    watchGetPaymentMethodsList(),
    watchOperationsExport(),
    watchCaptureRefundOperation()
    // @generator saga:fork
  ])
}
